import React, { useCallback, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { useTranslation } from "react-i18next";
import { resolveVideoInviteFromMessage } from "../../utils/videoInviteMessage";

/**
 * Convite de vídeo / Meet — layout mínimo, cores do tema / da bolha.
 * No card de toolkit do atendente (`xpro_video_invite`): copiar / usar no composer / entrar.
 *
 * @param {{ message: object, bubbleFg?: string, onAccentBubble?: boolean }} props
 */
const VideoInviteMessage = ({ message, bubbleFg, onAccentBubble = false }) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const invite = resolveVideoInviteFromMessage(message);

  const guestUrl = invite?.guestUrl || invite?.url || "";
  const agentUrl = invite?.agentUrl || invite?.url || "";
  const conversationId =
    message?.id_lead ?? message?.lead_id ?? message?.conversationId ?? null;
  const composeText =
    invite?.composeText ||
    (guestUrl
      ? `${t(
          invite?.requireCamera
            ? "video.inviteBodyOutboundCameraRequired"
            : "video.inviteBodyOutbound",
          invite?.requireCamera
            ? "Esta chamada exige câmera ligada. Toque no botão abaixo para entrar com o vídeo ativo — sem câmera neste dispositivo, não é possível participar."
            : "Toque no botão abaixo para entrar na chamada de vídeo com nossa equipe."
        )}\n${guestUrl}`
      : "");

  const handleCopy = useCallback(async () => {
    if (!guestUrl) return;
    try {
      await navigator.clipboard.writeText(guestUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }, [guestUrl]);

  const handleUseInComposer = useCallback(() => {
    if (!composeText && !guestUrl) return;
    const cta =
      invite?.label ||
      (invite?.requireCamera
        ? t("video.inviteCtaOutboundCameraRequired", "Entrar com a câmera")
        : t("video.inviteCtaOutbound", "Entrar na chamada"));
    const body =
      invite?.composeText?.split("\n")[0] ||
      t(
        invite?.requireCamera
          ? "video.inviteBodyOutboundCameraRequired"
          : "video.inviteBodyOutbound",
        invite?.requireCamera
          ? "Esta chamada exige câmera ligada. Toque no botão abaixo para entrar com o vídeo ativo — sem câmera neste dispositivo, não é possível participar."
          : "Toque no botão abaixo para entrar na chamada de vídeo com nossa equipe."
      );
    window.dispatchEvent(
      new CustomEvent("xpro-chat-compose", {
        detail: {
          conversationId: conversationId != null ? Number(conversationId) : undefined,
          setText: body,
          joinUrl: guestUrl,
          buttonMessage: guestUrl ? `${cta}|${guestUrl}` : undefined,
        },
      })
    );
  }, [conversationId, composeText, guestUrl, invite?.label, invite?.composeText, invite?.requireCamera, t]);

  if (!invite?.url) return null;

  const isMeet = invite.kind === "meet";
  const isToolkit = Boolean(invite.toolkit);
  const isTeamInvite = Boolean(invite.teamInvite);
  const title = isMeet
    ? t("video.inviteMeetTitle", "Google Meet")
    : isTeamInvite
      ? t("video.inviteTeamTitle", "Chamada da equipe")
      : t("video.inviteTitle", "Chamada de vídeo");
  const cta =
    invite.label ||
    (invite.requireCamera
      ? t("video.inviteCtaOutboundCameraRequired", "Entrar com a câmera")
      : isMeet
        ? t("video.inviteMeetCta", "Abrir Meet")
        : isTeamInvite
          ? t("video.inviteCtaTeam", "Entrar na chamada da equipe")
          : isToolkit
            ? t("video.inviteJoinCall", "Entrar na chamada")
            : t("video.inviteCta", "Abrir chamada"));

  const textColor = bubbleFg || "inherit";
  const accent = theme.palette.primary.main;

  const buttonSx = onAccentBubble
    ? {
        color: "inherit",
        borderColor: alpha("#ffffff", 0.55),
        bgcolor: alpha("#ffffff", 0.12),
        "&:hover": {
          borderColor: alpha("#ffffff", 0.85),
          bgcolor: alpha("#ffffff", 0.2),
        },
      }
    : {
        color: accent,
        borderColor: alpha(accent, 0.4),
        bgcolor: alpha(accent, 0.08),
        "&:hover": {
          borderColor: accent,
          bgcolor: alpha(accent, 0.14),
        },
      };

  const openUrl = isToolkit ? agentUrl : invite.url;

  return (
    <Box sx={{ color: textColor, maxWidth: isToolkit ? 280 : 240, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.85 }}>
        <VideocamOutlinedIcon sx={{ fontSize: 17, opacity: 0.9, flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, lineHeight: 1.25, color: "inherit" }}
        >
          {title}
        </Typography>
      </Box>

      {invite.requireCamera ? (
        <Typography
          variant="caption"
          sx={{ display: "block", mb: 1, opacity: 0.9, lineHeight: 1.35, fontWeight: 600 }}
        >
          {t(
            "video.inviteCameraRequiredBadge",
            "Câmera obrigatória para entrar — sem vídeo no dispositivo, não é possível participar."
          )}
        </Typography>
      ) : null}

      {isToolkit ? (
        <Typography
          variant="caption"
          sx={{ display: "block", mb: 1, opacity: 0.85, lineHeight: 1.35 }}
        >
          {t(
            "video.inviteToolkitHint",
            "Envie o link ao cliente pelo canal desejado ou copie para colar fora do XPro."
          )}
        </Typography>
      ) : null}

      <Stack spacing={0.75} alignItems="stretch">
        {isToolkit ? (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={handleCopy}
              startIcon={
                copied ? (
                  <CheckIcon sx={{ fontSize: "14px !important" }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: "14px !important" }} />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                lineHeight: 1.2,
                px: 1.15,
                py: 0.4,
                borderRadius: 1.25,
                minHeight: 30,
                justifyContent: "flex-start",
                ...buttonSx,
              }}
            >
              {copied
                ? t("video.inviteLinkCopied", "Link copiado")
                : t("video.inviteCopyLink", "Copiar link do cliente")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleUseInComposer}
              startIcon={<EditNoteOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                lineHeight: 1.2,
                px: 1.15,
                py: 0.4,
                borderRadius: 1.25,
                minHeight: 30,
                justifyContent: "flex-start",
                ...buttonSx,
              }}
            >
              {t("video.inviteUseInComposer", "Usar no campo de envio")}
            </Button>
          </>
        ) : null}

        <Button
          component="a"
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: "13px !important" }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            lineHeight: 1.2,
            px: 1.15,
            py: 0.4,
            borderRadius: 1.25,
            minHeight: 30,
            justifyContent: isToolkit ? "flex-start" : "center",
            ...buttonSx,
          }}
        >
          {cta}
        </Button>
      </Stack>
    </Box>
  );
};

export default VideoInviteMessage;
